import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3 } from "lucide-react";

const getBarColor = (value) => {
  if (value >= 7) return "bg-success";
  if (value >= 5) return "bg-warning";
  return "bg-destructive";
};

const getBarColorLight = (value) => {
  if (value >= 7) return "bg-success/20";
  if (value >= 5) return "bg-warning/20";
  return "bg-destructive/20";
};

// v1.1.0: Added authenticity, renamed engagement to engagement_quality
const metricLabels = {
  hook: { label: "Hook", description: "Opening impact" },
  value: { label: "Value", description: "Content worth" },
  authenticity: { label: "Authenticity", description: "Genuine voice" },
  engagement_quality: { label: "Engagement", description: "Interaction quality" },
  readability: { label: "Readability", description: "Ease of reading" },
  emotional_pull: { label: "Emotional Pull", description: "Emotional connection" },
};

// Metrics to display in order (excludes backward-compat duplicates)
const displayOrder = ['hook', 'value', 'authenticity', 'engagement_quality', 'readability', 'emotional_pull'];

export const BreakdownChart = ({ breakdown }) => {
  if (!breakdown || typeof breakdown !== 'object') return null;

  // Filter to only show metrics in displayOrder (avoids duplicate 'engagement')
  const metrics = displayOrder
    .filter(key => breakdown[key] !== undefined)
    .map(key => ({
      key,
      label: metricLabels[key]?.label || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      description: metricLabels[key]?.description || '',
      value: typeof breakdown[key] === 'number' ? breakdown[key] : 0,
    }));

  return (
    <Card className="border border-border shadow-md">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          Score Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {metrics.map((metric, index) => (
          <div 
            key={metric.key} 
            className="space-y-2"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {metric.label}
                </span>
                {metric.description && (
                  <span className="text-xs text-muted-foreground hidden sm:inline">
                    • {metric.description}
                  </span>
                )}
              </div>
              <span className="text-sm font-semibold text-foreground tabular-nums">
                {metric.value}/10
              </span>
            </div>
            
            <div 
              className={`relative h-2.5 rounded-full overflow-hidden ${getBarColorLight(metric.value)}`}
              role="progressbar"
              aria-valuenow={metric.value}
              aria-valuemin="0"
              aria-valuemax="10"
              aria-label={`${metric.label}: ${metric.value} out of 10`}
            >
              <div
                className={`absolute inset-y-0 left-0 rounded-full ${getBarColor(metric.value)} transition-all duration-500 ease-out`}
                style={{ width: `${(metric.value / 10) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default BreakdownChart;
